import {course_getAttendanceReport_Api} from 'apis'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {withReportTable} from 'views/Workspace/Examination/renderReportTable'
import CourseClassAttendanceReportFilter from '../components/filters/CourseClassAttendanceReportFilter'
import {useRouteSearchParam} from "../../../../envs/_current/history";
import {renderIf, renderIfElse} from "../../../Shared";
import Translate from "../../../../modules/local/Translate";
import {GoDotFill} from "react-icons/go";

const headers = [
    'fullname',
    'attend_count',
    'ea_count',
    'ua_count',
    'total classes',
    'cc_percent',
    'eligibility for exam',
]

const schemas = [
    {
        name: 'fullname',
        value_path: 'fullname',
        className: 'font-medium text-color-000 whitespace-no-wrap',
    },
    {
        name: 'attend_count',
        value_path: 'attend_count',
        className: 'text-color-100',
    },
    {
        name: 'excused_absence_count',
        value_path: 'excused_absence_count',
        className: 'text-color-100',
    },
    {
        name: 'unexcused_absence_count',
        value_path: 'unexcused_absence_count',
        className: 'text-color-100',
    },
    {
        name: 'total_count',
        value_path: 'total_count',
        className: 'text-color-100',
    },
    {
        name: 'cc_percent',
        value_path: 'cc_percent',
        className: 'text-color-100',
        render: (value, data) => (
            <Translate>
                {t => (
                    renderIfElse(
                        value === null || value === undefined,
                        <span className="font-normal text-xs text-color-400 italic capitalize">{t('not update')}</span>,
                        <span className="font-semibold text-xs text-color-000">{`${value} %`}</span>
                    )
                )}
            </Translate>
        ),
    },
    {
        name: 'status',
        value_path: 'status',
        className: 'text-color-100',
        render: (value, data) => (
            <Translate>
                {t => (
                    renderIfElse(
                        _.get(data, 'status', null) === null,
                        <span className="font-normal text-xs text-color-400 italic capitalize">{t('not update')}</span>,
                        <div className="ml-1 space-y-1 mt-1">
                            {renderIfElse(
                                !!value,
                                <span className="font-bold text-sm text-green-500">{t('yes')}</span>,
                                <span className="font-medium text-sm text-red-500">{t('no')}</span>
                            )}
                        </div>
                    )
                )}
            </Translate>
        ),
    },
]

const CourseClassAttendanceReport = ({
                                         owner_id,
                                         owner_type,
                                         filter_actions = {
                                             sortBy: 'sort-by',
                                             keyword: 'keyword',
                                             course_id: 'course-id',
                                             subject_id: 'subject-id',
                                             course_class_id: 'class-id',
                                         },
                                     }) => {
    const t = useTranslate()

    const location = useLocation()

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const course_id = useRouteSearchParam(filter_actions.course_id)

    const subject_id = useRouteSearchParam(filter_actions.subject_id)

    const course_class_id = useRouteSearchParam(filter_actions.course_class_id)

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            subject_id, course_class_id,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, subject_id, course_class_id, location.state]
    )

    const [pager_widget, {widget_header}] = usePaginationHelper({
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onSortChange(sortBy)
            onFilterChange({
                course_id,
                subject_id,
                course_class_id,
            })
        },
        pagerInfo: {
            query: bindQueryParam({
                id: course_class_id,
            }),
            values: {
                subject_id,
            },
            PaginationMeta: NullAsDefault,
            apiInfo: course_getAttendanceReport_Api,
            ...withReportTable(headers, schemas),
        },
        withKey: createPagerKey(refresh_deps),
        dependencies: [owner_id, owner_type],
        filter_params: {
            owner_id,
            owner_type,
            course_id,
            subject_id,
            course_class_id,
        },
        renderSortBy: NullAsDefault,
        SortByWidget: NullAsDefault,
        renderSearchBar: NullAsDefault,
        isToggleDefault: true,
        query_params: filter_actions,
        AdvancedFilter: CourseClassAttendanceReportFilter,
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col">
                {filterBar}
                {renderIf(
                    course_class_id,
                    <div className="flex flex-col mb-3">
                        <span className="font-bold text-sm text-color-200 italic">
                            {`* ${t('note')} : `}
                        </span>
                        <div className="ml-2">
                            <div className="space-x-1 text-xs italic">
                                <GoDotFill size={10} className="text-color-500"/>
                                <span className="font-medium text-color-100 whitespace-no-wrap">
                                    {`${t('cc_percent')} : `}
                                </span>
                                <span className="text-color-400">
                                    {t('cc_percent attendance description')}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        ),
    })

    return (
        <div className="space-y-3">
            <div className="font-semibold text-primary tracking-wide uppercase">
                {t('report')}
            </div>
            <div className="border rounded-lg p-3">
                <div className="flex flex-col">
                    <div className="text-center font-bold text-lg text-color-000 uppercase tracking-widest mb-4">
                        {t('attendance statistics by class')}
                    </div>
                    {course_class_id && subject_id ? pager_widget : widget_header}
                </div>
            </div>
        </div>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(CourseClassAttendanceReport)
